import { ReactNode, useEffect } from 'react'
import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import {
  Text,
  GreyGrey20,
  GreyGrey70,
  Primary13Regular,
  IconEdit,
  Box,
  Flex,
  Grid,
  GridItem
} from '@sequencehq/core-components'
import {
  editingRowIdAtom,
  editingRowLockedAtom,
  isEditingEnabledAtom
} from './atoms'
import { isSavePendingAtom } from 'Invoices/atoms/invoiceEditing.atom'

export const EditableSection = ({
  children,
  isEditingEnabled = true
}: {
  children: ReactNode
  isEditingEnabled?: boolean
}) => {
  const setIsEditingEnabled = useSetAtom(isEditingEnabledAtom)

  useEffect(() => {
    setIsEditingEnabled(isEditingEnabled)
  }, [isEditingEnabled, setIsEditingEnabled])

  return (
    <Flex flexDir="column" gap="4px">
      {children}
    </Flex>
  )
}

EditableSection.Row = ({
  id,
  label,
  children,
  isEditing: isEditingOverride,
  isEditable: isEditableOverride,
  padding = '8px',
  'data-testid': dataTestId
}: {
  id: string
  children: ReactNode | ((isEditing: boolean) => ReactNode)
  label: ReactNode
  isEditing?: boolean // override the internal state
  isEditable?: boolean // override the internal state
  padding?: string
  'data-testid': string
}) => {
  const [editingRowId, setEditingRowId] = useAtom(editingRowIdAtom)
  const isEditingLocked = useAtomValue(editingRowLockedAtom)
  const isEditingEnabled = useAtomValue(isEditingEnabledAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)
  const isEditable =
    isEditableOverride ??
    (isEditingEnabled &&
      typeof children === 'function' &&
      (!isSavePending || (isSavePending && editingRowId === id)))
  const isEditing = isEditingOverride ?? (editingRowId === id && isEditable)

  return (
    <Grid alignItems="center" gridTemplateColumns="180px 1fr" gap="16px">
      <GridItem alignSelf="flex-start" padding="8px 0px">
        {typeof label === 'string' ? (
          <EditableSection.RowLabel data-testid={`${dataTestId}.label`}>
            {label}
          </EditableSection.RowLabel>
        ) : (
          label
        )}
      </GridItem>
      <GridItem
        _hover={{
          background: isEditing || !isEditable ? 'transparent' : GreyGrey20,
          transition:
            isEditing || !isEditable ? 'none' : 'background 0.2s ease-in-out'
        }}
        transition={isEditing ? 'none' : 'background 0.05s ease-in-out'}
        borderRadius="6px"
        className="group"
        cursor={isEditable ? 'pointer' : 'default'}
        onClick={e => {
          e.stopPropagation()
          if (isEditable && !isEditing && !isEditingLocked) {
            setEditingRowId(id)
          }
        }}
        data-testid={dataTestId}
      >
        <Flex padding={isEditing ? '0px' : padding}>
          <Box flex={1} position="relative">
            {typeof children === 'function' ? children(isEditing) : children}
          </Box>
          {!isEditing && isEditable && (
            <Box
              opacity={0}
              _groupHover={{
                opacity: 1,
                transition: 'opacity 0.2s ease-in-out'
              }}
              transition={'opacity 0.05s ease-in-out'}
            >
              <IconEdit color={GreyGrey70} />
            </Box>
          )}
        </Flex>
      </GridItem>
    </Grid>
  )
}

EditableSection.RowLabel = ({
  children,
  'data-testid': dataTestId
}: {
  children: string
  'data-testid'?: string
}) => {
  return (
    <Text
      {...Primary13Regular}
      color={GreyGrey70}
      userSelect="none"
      data-testid={dataTestId}
    >
      {children}
    </Text>
  )
}
